import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { verifyPaymentSession } from "@/services/digitalContent/stripeService";
import { verifyPayment } from "@/services/mentorSessionsService";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'retrying'>('loading');
  const [message, setMessage] = useState('');
  const [paymentType, setPaymentType] = useState<'mentorship' | 'digital_content' | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [maxRetries] = useState(5);

  useEffect(() => {
    const verifyPaymentStatus = async () => {
      try {
        const sessionId = searchParams.get('session_id');
        
        if (!sessionId) {
          setStatus('error');
          setMessage('ID da sessão de pagamento não encontrado');
          return;
        }

        console.log('🔍 [PaymentSuccess] Verificando pagamento:', sessionId);

        // Determinar o tipo de pagamento verificando se é uma sessão de mentoria
        // Buscar pagamento que tenha o stripe_payment_id correspondente
        const { data: paymentData, error: paymentError } = await supabase
          .from('payments')
          .select('id, session_id, service_type')
          .eq('stripe_payment_id', sessionId)
          .single();
        
        // Buscar dados da sessão se for mentoria
        let sessionData = null;
        if (!paymentError && paymentData && paymentData.service_type === 'mentorship') {
          const { data: session } = await supabase
            .from('sessions')
            .select('id, payments(stripe_payment_id)')
            .eq('id', paymentData.session_id)
            .single();
          
          if (session) {
            sessionData = session;
          }
        }

        if (paymentData && paymentData.service_type === 'mentorship' && sessionData) {
          // É um pagamento de mentoria
          setPaymentType('mentorship');
          await verifyMentorshipPayment(sessionData.id, sessionData);
        } else {
          // É um pagamento de conteúdo digital
          setPaymentType('digital_content');
          await verifyDigitalContentPayment(sessionId);
        }

      } catch (error: any) {
        console.error('❌ [PaymentSuccess] Erro ao verificar pagamento:', error);
        setStatus('error');
        setMessage('Erro ao verificar status do pagamento: ' + error.message);
      }
    };

    const verifyMentorshipPayment = async (mentorshipSessionId: string, sessionData: any) => {
      try {
        console.log('🏆 [PaymentSuccess] Verificando pagamento de mentoria:', mentorshipSessionId);

        const payment = sessionData.payments?.[0];
        if (!payment?.stripe_payment_id) {
          setStatus('error');
          setMessage('Informações de pagamento não encontradas');
          return;
        }

        // Usar a função específica para verificar pagamento de mentoria
        const result = await verifyPayment(mentorshipSessionId, payment.stripe_payment_id);

        if (result.status === 'completed') {
          setStatus('success');
          setMessage('Pagamento confirmado! Sua mentoria foi agendada com sucesso.');
          
          toast({
            title: "Pagamento aprovado!",
            description: "Sua mentoria foi confirmada. Você receberá mais detalhes por email.",
            variant: "default"
          });

          // Redirecionar para mentorias compradas após 3 segundos
          setTimeout(() => {
            navigate('/painel-jogador/mentorias-compradas');
          }, 3000);

        } else if (result.status === 'pending') {
          if (retryCount < maxRetries) {
            setStatus('retrying');
            setMessage(`Aguardando confirmação do pagamento... (tentativa ${retryCount + 1}/${maxRetries})`);
            setRetryCount(prev => prev + 1);
            
            // Tentar novamente em 3 segundos
            setTimeout(() => verifyMentorshipPayment(mentorshipSessionId, sessionData), 3000);
          } else {
            // Após max tentativas, tentar fallback
            await tryFallbackProcessing(mentorshipSessionId, 'mentorship');
          }

        } else {
          setStatus('error');
          setMessage('Pagamento não foi confirmado. Entre em contato com o suporte.');
        }

      } catch (error: any) {
        console.error('❌ [PaymentSuccess] Erro ao verificar pagamento de mentoria:', error);
        
        if (retryCount < maxRetries) {
          setStatus('retrying');
          setMessage(`Erro na verificação, tentando novamente... (${retryCount + 1}/${maxRetries})`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => verifyMentorshipPayment(mentorshipSessionId, sessionData), 3000);
        } else {
          await tryFallbackProcessing(mentorshipSessionId, 'mentorship');
        }
      }
    };

    const verifyDigitalContentPayment = async (stripeSessionId: string) => {
      try {
        console.log('📱 [PaymentSuccess] Verificando pagamento de conteúdo digital:', stripeSessionId);

        // Verificar status do pagamento usando a função original
        const result = await verifyPaymentSession(stripeSessionId);

        if (result.status === 'completed') {
          setStatus('success');
          setMessage('Pagamento confirmado! Seu conteúdo foi liberado.');
          
          toast({
            title: "Pagamento aprovado!",
            description: "Seu conteúdo digital foi adicionado à sua biblioteca.",
            variant: "default"
          });

          // Redirecionar para a biblioteca após 3 segundos
          setTimeout(() => {
            navigate('/painel-jogador/meus-conteudos');
          }, 3000);

        } else if (result.status === 'pending') {
          if (retryCount < maxRetries) {
            setStatus('retrying');
            setMessage(`Aguardando confirmação do pagamento... (tentativa ${retryCount + 1}/${maxRetries})`);
            setRetryCount(prev => prev + 1);
            
            // Tentar novamente em 3 segundos
            setTimeout(() => verifyDigitalContentPayment(stripeSessionId), 3000);
          } else {
            // Após max tentativas, tentar fallback
            await tryFallbackProcessing(stripeSessionId, 'digital_content');
          }

        } else {
          setStatus('error');
          setMessage('Pagamento não foi confirmado. Entre em contato com o suporte.');
        }

      } catch (error: any) {
        console.error('❌ [PaymentSuccess] Erro ao verificar pagamento de conteúdo digital:', error);
        
        if (retryCount < maxRetries) {
          setStatus('retrying');
          setMessage(`Erro na verificação, tentando novamente... (${retryCount + 1}/${maxRetries})`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => verifyDigitalContentPayment(stripeSessionId), 3000);
        } else {
          await tryFallbackProcessing(stripeSessionId, 'digital_content');
        }
      }
    };

    /**
     * 🛡️ SISTEMA DE FALLBACK AUTOMÁTICO
     * 
     * Quando o webhook falha ou não processa o pagamento,
     * este sistema tenta processar manualmente
     */
    const tryFallbackProcessing = async (sessionId: string, type: 'mentorship' | 'digital_content') => {
      try {
        console.log(`🛡️ [PaymentSuccess] Iniciando processamento de fallback para ${type}:`, sessionId);
        setStatus('loading');
        setMessage('Processando pagamento automaticamente...');

        // Chamar sistema de monitoramento para processar pagamento
        const { data, error } = await supabase.functions.invoke('payment-monitor', {
          body: { 
            sessionId,
            type,
            forceProcess: true 
          }
        });

        if (error) {
          throw new Error(`Erro no processamento automático: ${error.message}`);
        }

        if (data?.success) {
          setStatus('success');
          setMessage('Pagamento processado com sucesso! Acesso liberado.');
          
          toast({
            title: "Pagamento confirmado!",
            description: "Seu pagamento foi processado automaticamente.",
            variant: "default"
          });

          // Redirecionar baseado no tipo
          setTimeout(() => {
            if (type === 'mentorship') {
              navigate('/painel-jogador/mentorias-compradas');
            } else {
              navigate('/painel-jogador/meus-conteudos');
            }
          }, 3000);

        } else {
          throw new Error('Falha no processamento automático');
        }

      } catch (error: any) {
        console.error('❌ [PaymentSuccess] Erro no processamento de fallback:', error);
        setStatus('error');
        setMessage(`Não foi possível processar o pagamento automaticamente. Entre em contato com o suporte. Erro: ${error.message}`);
      }
    };

    verifyPaymentStatus();
  }, [searchParams, navigate, toast, retryCount, maxRetries]);

  const handleGoToLibrary = () => {
    if (paymentType === 'mentorship') {
      navigate('/painel-jogador/mentorias-compradas');
    } else {
      navigate('/painel-jogador/meus-conteudos');
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setStatus('loading');
    setMessage('Verificando pagamento novamente...');
    // O useEffect será executado novamente
  };

  const handleContactSupport = () => {
    // Abrir email ou WhatsApp para suporte
    const subject = encodeURIComponent('Problema com Pagamento - Arena Pro Player');
    const body = encodeURIComponent(`Olá, estou com problema no pagamento. Session ID: ${searchParams.get('session_id')}`);
    window.open(`mailto:suporte@arenaproplayer.com?subject=${subject}&body=${body}`);
  };

  return (
    <div className="min-h-screen bg-[#0f141c] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gradient-to-r from-[#ffffff08] to-[#0f141c] backdrop-blur-sm border border-[#ffffff10] rounded-2xl p-8 text-center">
        
        {/* Ícone de Status */}
        <div className="mb-6">
          {status === 'loading' && (
            <Loader2 className="h-16 w-16 text-[#00B6F0] mx-auto animate-spin" />
          )}
          {status === 'success' && (
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          )}
          {status === 'error' && (
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto" />
          )}
          {status === 'retrying' && (
            <RefreshCw className="h-16 w-16 text-yellow-500 mx-auto animate-spin" />
          )}
        </div>

        {/* Título */}
        <h1 className="text-2xl font-bold text-white mb-4">
          {status === 'loading' && 'Verificando Pagamento...'}
          {status === 'success' && 'Pagamento Confirmado!'}
          {status === 'error' && 'Problema no Pagamento'}
          {status === 'retrying' && 'Processando...'}
        </h1>

        {/* Mensagem */}
        <p className="text-white/80 mb-6">
          {message}
        </p>

        {/* Botões de Ação */}
        <div className="space-y-3">
          {status === 'success' && (
            <Button 
              onClick={handleGoToLibrary}
              className="w-full bg-[#00B6F0] hover:bg-[#00B6F0]/80 text-black font-semibold"
            >
              {paymentType === 'mentorship' ? 'Ver Minhas Mentorias' : 'Ver Meus Conteúdos'}
            </Button>
          )}

          {status === 'error' && (
            <>
              <Button 
                onClick={handleRetry}
                className="w-full bg-[#00B6F0] hover:bg-[#00B6F0]/80 text-black font-semibold"
              >
                Tentar Novamente
              </Button>
              <Button 
                onClick={handleContactSupport}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                Contatar Suporte
              </Button>
            </>
          )}

          {status === 'retrying' && (
            <div className="text-sm text-white/60">
              Aguarde enquanto processamos seu pagamento...
            </div>
          )}
        </div>

        {/* Informações de Debug (apenas em desenvolvimento) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-6 p-4 bg-black/20 rounded-lg text-xs text-white/60">
            <div>Session ID: {searchParams.get('session_id')}</div>
            <div>Tipo: {paymentType}</div>
            <div>Tentativas: {retryCount}/{maxRetries}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;